import { useQueryClient, useIsFetching } from '@tanstack/react-query';
import { useSeoMeta } from '@unhead/react';

import { ChainPanel, type ChainQueries } from '@/components/compare/ChainPanel';
import { ComparisonHeader } from '@/components/compare/ComparisonHeader';
import { TugOfWar } from '@/components/compare/TugOfWar';
import {
  useDifficultyAdjustment,
  useForkStatus,
  useHashrate,
  useMempool,
  useRecentBlocks,
  useRecommendedFees,
} from '@/hooks/useChainData';
import type { ChainId } from '@/lib/mempool';

function useChainQueries(chainId: ChainId): ChainQueries {
  return {
    blocks: useRecentBlocks(chainId),
    difficulty: useDifficultyAdjustment(chainId),
    hashrate: useHashrate(chainId),
    mempool: useMempool(chainId),
    fees: useRecommendedFees(chainId),
  };
}

const Index = () => {
  useSeoMeta({
    title: 'Bitcoin Chain Compare — Standard vs BIP-110',
    description:
      'Live side-by-side comparison of the standard Bitcoin chain (mempool.space) and the BIP-110 chain (mempool.guide): block height, hashrate, difficulty adjustment, mempool, fees and latest blocks.',
    ogType: 'website',
    ogTitle: 'Bitcoin Chain Compare — Standard vs BIP-110',
    ogDescription:
      'Live side-by-side comparison of the standard Bitcoin chain and the BIP-110 chain: height, hashrate, difficulty adjustment, mempool, fees and more.',
    ogUrl: 'https://bip110-chain-compare.shakespeare.wtf/',
    ogImage: 'https://bip110-chain-compare.shakespeare.wtf/og-image.jpg',
    twitterCard: 'summary_large_image',
    twitterImage: 'https://bip110-chain-compare.shakespeare.wtf/og-image.jpg',
  });

  const queryClient = useQueryClient();
  const isFetching = useIsFetching({ queryKey: ['mempool'] }) > 0;

  const standard = useChainQueries('standard');
  const bip110 = useChainQueries('bip110');

  const standardTip = standard.blocks.data?.[0];
  const bip110Tip = bip110.blocks.data?.[0];

  const forkQuery = useForkStatus(standardTip?.height, bip110Tip?.height);

  const updatedAt = Math.max(
    standard.blocks.dataUpdatedAt,
    bip110.blocks.dataUpdatedAt,
  );

  const handleRefresh = () => {
    // Invalidation refetches active queries only; per-endpoint stale times
    // still apply, and TanStack's retry backoff protects the API on errors.
    queryClient.invalidateQueries({ queryKey: ['mempool'] });
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-[1400px] py-8 sm:py-10">
        {/* Page header */}
        <header className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Bitcoin Chain Compare
          </h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground sm:text-base">
            Live side-by-side telemetry for the{' '}
            <span className="font-medium text-amber-600 dark:text-amber-400">standard Bitcoin chain</span>{' '}
            (data from mempool.space) and the{' '}
            <span className="font-medium text-violet-600 dark:text-violet-400">BIP-110 chain</span>{' '}
            (data from mempool.guide, a mempool.space fork). BIP-110 is a soft-fork proposal that
            temporarily tightens data-carrier rules; if its rules activate, nodes enforcing them
            can follow a different chain than standard nodes. This dashboard tracks both chains'
            height, hashrate, difficulty adjustment, mempool, fees and latest blocks.
          </p>
        </header>

        {/* Hero comparison */}
        <ComparisonHeader
          standardTip={standardTip}
          bip110Tip={bip110Tip}
          forkQuery={forkQuery}
          isFetching={isFetching}
          updatedAt={updatedAt}
          onRefresh={handleRefresh}
        />

        {/* Tug of war: deficit vs. survival odds */}
        <div className="mt-6">
          <TugOfWar
            standardTip={standardTip}
            bip110Tip={bip110Tip}
            standardHashrate={standard.hashrate.data?.currentHashrate}
            bip110Hashrate={bip110.hashrate.data?.currentHashrate}
          />
        </div>

        {/* Per-chain panels */}
        <div className="mt-6 grid gap-6 xl:grid-cols-2">
          <ChainPanel chainId="standard" queries={standard} />
          <ChainPanel chainId="bip110" queries={bip110} />
        </div>

        {/* Footer */}
        <footer className="mt-10 border-t pt-6 text-xs leading-relaxed text-muted-foreground">
          <p>
            Sources:{' '}
            <a
              href="https://mempool.space"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2 transition-colors hover:text-foreground"
            >
              mempool.space
            </a>{' '}
            (standard chain) and{' '}
            <a
              href="https://mempool.guide"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2 transition-colors hover:text-foreground"
            >
              mempool.guide
            </a>{' '}
            (BIP-110 chain). Data auto-refreshes every 60 seconds to respect each API's rate
            limits; hashrate history and the fork check refresh every 5 minutes.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <a
              href="https://marlowe.shakespeare.wtf"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2 transition-colors hover:text-foreground"
            >
              Vibed with Marlowe
            </a>
            <a
              href="https://marlowe.shakespeare.wtf/clone?url=nostr%3A%2F%2Fnpub14rg4vrt2v374q95ezeeydu3hkdhmzglcj950mggacap4x0lv0gyq04wun7%2Frelay.ngit.dev%2Fbip110-chain-compare"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-opacity hover:opacity-80"
            >
              <img
                src="https://marlowe.shakespeare.wtf/marlowe-badge.svg"
                alt="Edit with Marlowe"
                className="h-auto"
              />
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Index;
